import { useState } from 'react';
import CreateInviteForm from './CreateInviteForm';
import CreateInviteSuccess from './CreateInviteSuccess';
import { useModalScrollLock } from '../../hooks/useModalScrollLock';
import styles from './CreateInviteModal.module.css';

/**
 * CreateInviteModal Component
 *
 * Modal for creating budget invites
 * - Select budget (only budgets user owns)
 * - Select access level (member or elevated_member)
 * - Generate and display invite code
 *
 * Props:
 * - userEmail (string): Current user's email
 * - budgets (array): Budgets user owns
 * - onClose (function): Called when modal is closed
 * - onSuccess (function): Called when invite is successfully created
 * - getAccessTokenSilently (function): Auth0 function to get access token
 */
function CreateInviteModal({ _userEmail, budgets, onClose, onSuccess, getAccessTokenSilently }) {
    const [selectedBudget, setSelectedBudget] = useState(budgets.length > 0 ? budgets[0].budgetKey : '');
    const [accessLevel, setAccessLevel] = useState('member');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [createdCode, setCreatedCode] = useState(null);

    // Lock body scroll when modal is open (prevents background scrolling on mobile)
    // This modal is always "open" when rendered
    useModalScrollLock(true);

    const handleDone = () => {
        onSuccess();
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(createdCode);
        alert('Code copied to clipboard!');
    };

    const handleCreate = async () => {
        try {
            setLoading(true);
            setError(null);

            const token = await getAccessTokenSilently();
            const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';
            const response = await fetch(`${API_BASE_URL}/invites/create`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    budgetKey: parseInt(selectedBudget),
                    accessLevel
                })
            });

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.error || 'Failed to create invite');
            }

            const data = await response.json();
            setCreatedCode(data.inviteCode);
        } catch (err) {
            // Error creating invite
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={styles.overlay}>
            <div className={styles.modal}>
                <h2 className={styles.title}>Create Budget Invite</h2>

                {!createdCode ? (
                    <CreateInviteForm
                        budgets={budgets}
                        selectedBudget={selectedBudget}
                        setSelectedBudget={setSelectedBudget}
                        accessLevel={accessLevel}
                        setAccessLevel={setAccessLevel}
                        handleCreate={handleCreate}
                        loading={loading}
                        error={error}
                        onClose={onClose}
                    />
                ) : (
                    <CreateInviteSuccess
                        createdCode={createdCode}
                        copyToClipboard={copyToClipboard}
                        handleDone={handleDone}
                    />
                )}
            </div>
        </div>
    );
}

export default CreateInviteModal;
